import { FireCMSTranslations } from "../types/translations";

/**
 * English baseline translations for @firecms/core.
 *
 * This is the single source of truth for all default UI strings.
 * Override any key via the `translations` prop on FireCMS entry-point components.
 *
 * @group Core
 */
export const en: FireCMSTranslations = {
    // ─── Form actions ────────────────────────────────────────────
    save: "Save",
    create: "Create",
    create_copy: "Create copy",
    save_and_close: "Save and close",
    create_copy_and_close: "Create copy and close",
    create_and_close: "Create and close",
    discard: "Discard",
    clear: "Clear",
    cancel: "Cancel",

    // ─── Entity actions ──────────────────────────────────────────
    edit: "Edit",
    copy: "Copy",
    delete: "Delete",

    // ─── Delete dialog ───────────────────────────────────────────
    delete_confirmation_title: "Delete?",
    delete_confirmation_body: "This will delete the entity. Are you sure?",
    delete_multiple_confirmation_body: "This will delete the selected entities. Are you sure?",

    // ─── Unsaved-changes dialog ───────────────────────────────────
    unsaved_changes_title: "Unsaved changes",
    unsaved_changes_body: "You have unsaved changes. Do you want to discard them?",
    discard_changes: "Discard changes",
    keep_editing: "Keep editing",

    // ─── Collection table / toolbar ──────────────────────────────
    search: "Search",
    find_by_id: "Find by ID",
    find_entity_by_id: "Find entity by ID",
    filter: "Filter",
    filters: "Filters",
    clear_filter: "Clear filter",
    clear_filter_sort: "Clear filter/sort",
    clear_sort: "Clear sort",
    no_items: "No items",
    no_entries_found: "No entries found",
    all_entries_loaded: "All {{count}} entries loaded",
    create_your_first_entry: "Create your first entry",
    no_results_filter_sort: "No results with the applied filter/sort",
    add: "Add",
    remove: "Remove",
    multiple_entities: "Multiple entities",
    unsaved_changes: "You have unsaved changes in this {{collectionName}}.",
    so_empty: "So empty...",
    no_results: "No results with the applied filter/sort",
    refresh_data: "Refresh data",
    dark_mode: "Dark",
    light_mode: "Light",
    system_mode: "System",
    ok: "Ok",
    save_collection_config: "Save collection structure",
    search_for_more_icons: "Search for more icons…",
    ai_modified: "AI modified",
    size_label: "Size",
    group_by: "Group by",
    initialize_kanban_order: "Initialize Kanban Order",
    copy_id: "Copy ID",
    add_specific: "Add {{name}}",
    select_specific: "Select {{name}}",
    select_from: "Select from {{name}}",
    done: "Done",
    log_out: "Log out",
    license_needed: "License needed",
    license_description: "You need a valid license to use FireCMS PRO. Please reach out at {{email}} for more information.",
    column_cannot_be_edited: "This column can't be edited directly",
    close: "Close",
    unsaved_local_changes: "Unsaved Local changes",
    unsaved_local_changes_description: "This document was edited locally and has unsaved changes. These local changes will be lost if you don't apply them.",
    preview_changes: "Preview Changes",
    apply_changes: "Apply Changes",
    discard_local_changes: "Discard Local Changes",
    preview_local_changes: "Preview Local Changes",
    preview_local_changes_description: "These are the local changes that will be applied to the form.",
    type: "Type",
    string: "string",
    number: "number",
    boolean: "boolean",
    date: "date",
    map: "map",
    array: "array",
    arrays_of_arrays_not_supported: "Arrays of arrays are not supported.",
    data_type_not_supported: "Data type {{dataType}} not supported yet",
    passkey_error_unsupported: "Your device or browser doesn't support passkeys.",

    admin: "Admin",
    home: "Home",
    this_form_has_errors: "This form has errors",
    error_loading_navigation: "Error loading navigation",
    error_loading_auth: "Error loading auth",
    this_form_has_been_modified: "This form has been modified",
    current_form_in_sync: "The current form is in sync with the database",
    open_in_console: "Open in the console",
    collection_does_not_exist: "The specified collection does not exist. Check console",
    unexpected_value: "Unexpected value",
    unexpected_value_click_to_edit: "Unexpected value. Click to edit",
    unexpected_reference_value: "Unexpected reference value. Click to edit",
    copy_url_to_clipboard: "Copy url to clipboard",
    open_image_in_new_tab: "Open image in new tab",
    reference_not_set: "Reference not set",
    reference_does_not_exist: "Reference does not exist",
    entity_not_found: "Entity not found",
    file_not_found: "File not found",
    unsaved_changes_in_entity: "You have unsaved changes in this entity.",
    delete_this_role: "Delete this role",
    no_roles_yet: "You don't have any roles yet.",
    create_default_roles: "Create default roles",
    delete_role_confirmation: "Are you sure you want to delete this role?",
    delete_this_user: "Delete this user",
    no_users_yet: "There are no users yet",
    add_logged_user_as_admin: "Add the logged user as an admin",
    add_current_user_as_admin: "Add current user as admin",
    create_default_roles_and_add_admin: "Create default roles and add current user as admin",
    delete_user_confirmation: "Are you sure you want to delete this user?",
    create_your_users_and_roles: "Create your users and roles",
    no_users_or_roles_defined: "You have no users or roles defined. You can create default roles and add the current user as admin.",
    save_before_changing_schema: "You need to save the document before changing the schema",
    edit_schema_for_this_form: "Edit schema for this form",
    no_permissions_to_edit_collection: "You don't have permissions to edit this collection",
    browser_does_not_support_audio: "Your browser does not support the audio element.",
    user_not_found: "User not found: {{value}}",

    // ─── Collection view actions ──────────────────────────────────
    delete_selected: "Delete",
    cannot_delete_selected: "You have selected at least one entity you cannot delete",

    // ─── Array / field containers ─────────────────────────────────
    add_entry: "Add",
    add_on_top: "Add on top",
    add_below: "Add below",
    add_to_field: "Add to {{fieldName}}",
    value: "Value",
    key: "Key",
    drag_drop_multiple: "Drag 'n' drop some files here, or click to select files. Drag to reorder.",
    drag_drop_single: "Drag 'n' drop a file here, or click to select one",

    // ─── Navigation / scaffold ────────────────────────────────────
    open_menu: "Open menu",
    close_drawer: "Close drawer",
    navigation_drawer: "Navigation drawer",

    // ─── Error states ─────────────────────────────────────────────
    error: "Error",
    error_uploading_file: "Error uploading file",
    error_deleting: "Error deleting",
    error_before_delete: "Error before delete",
    error_updating_asset: "Error updating asset",
    error_deleting_asset: "Error deleting asset",
    error_firestore_index: "A Firestore index is required for this query.",
    create_index: "Create Index",
    value_is_not_reference: "Value is not a reference.",
    click_to_edit: "Click to edit",
    data_is_not_array_of_references: "Data is not an array of references",

    // ─── Misc ─────────────────────────────────────────────────────
    loading: "Loading",
    local_changes_applied: "Local changes applied to the form",
    local_changes_discarded: "Local changes discarded",
    are_you_sure_leave: "Are you sure you want to leave this page?",
    see_console_details: "See console for more details.",
    drop_here_create_group: "Drop here to create a new group",
    filter_for_null_values: "Filter for null values",
    value_updated_elsewhere: "This value has been updated elsewhere",
    add_property: "Add property",
    edit_name: "Edit {{name}}",
    this_entity_not_exist: "This entity does not exist in the database",
    internal_error: "Internal ERROR",

    // ─── Rename group dialog ──────────────────────────────────────
    rename_group: "Rename Group",
    group_name_label: "Group Name",
    group_name_empty_error: "Group name cannot be empty.",
    group_name_exists_error: "This group name already exists.",

    // ─── Search ───────────────────────────────────────────────────
    search_collections: "Search collections",

    // ─── Navigation groups ────────────────────────────────────────
    views_group: "Views",

    // ─── Entity Edit View ─────────────────────────────────────────
    youd_need_to_save_before_additional_collections: "You need to save your entity before adding additional collections",

    // ─── Not Found Page ───────────────────────────────────────────
    page_not_found: "Page not found",
    page_not_found_body: "This page does not exist or you may not have access to it",
    back_to_home: "Back to home",

    // ─── Collection Editor ─────────────────────────────────────────
    default_collection_view: "Default collection view",
    table_view: "Table",
    cards_view: "Cards",
    kanban_view: "Kanban",
    choose_how_entities_displayed_default: "Choose how entities should be displayed by default",

    document_view: "Document view",
    side_panel: "Side panel",
    full_screen: "Full screen",
    should_documents_opened_full_screen: "Should documents be opened full screen or in an inline side dialog",

    select_custom_view: "Select custom view",
    no_custom_views_defined: "No custom views defined. Define your custom views in the customization settings, before using this dialog.",

    select_custom_action: "Select custom action",
    no_custom_actions_defined: "No custom actions defined. Define your custom actions in the customization settings, before using this dialog.",

    no_collections_found: "No collections found",
    start_building_collections: "Start building collections in FireCMS easily. Map them to your existing database data, import from files, or use our templates.",
    create_first_collection: "Create your first collection",
    define_collections_programmatically: "You can also define collections programmatically.",
    edit_collection: "Edit collection",
    no_permissions_edit_collection: "You don't have permissions to edit this collection",
    no_permissions_create_collection: "You don't have permissions to create collections",
    create_collection: "Create collection",
    update_collection: "Update collection",

    new_collection: "New collection",
    add_new_collection: "Add new collection",
    collection_with_name: "{{name}} collection",
    change_icon: "Change icon",
    is_subcollection_of: "This is a subcollection of",
    name: "Name",
    collection_name_description: "Name of this collection, usually a plural name (e.g. Products)",
    path: "Path",
    relative_path_to_parent: "Relative path to the parent (no need to include the parent path)",
    path_in_database: "Path that this collection is stored in, in the database",
    singular_name: "Singular name",
    singular_name_description: "Optionally define a singular name for your entities",
    description: "Description",
    description_of_collection: "Description of the collection, you can use markdown",
    collection_id: "Collection ID",
    collection_id_description: "This ID identifies this collection. Typically the same as the path.",
    collection_group: "Collection group",
    collection_group_description: "A collection group consists of all collections with the same path. This allows you to query over multiple collections at once.",
    advanced_settings: "Advanced settings",
    doc_history_global: "Document history revisions enabled if enabled globally",
    doc_history_enabled: "Document history revisions ENABLED",
    doc_history_not_enabled: "Document history revisions NOT enabled",
    doc_history_description: "When enabled, each document in this collection will have a history of changes. This is useful for auditing purposes. The data is stored in a subcollection of the document in your database, called __history.",
    document_id_generation: "Document IDs generation",
    code_defined: "Code defined",
    users_must_define_id: "Users must define an ID",
    users_can_define_id: "Users can define an ID, but it is not required",
    doc_id_auto_generated: "Document ID is generated automatically",
    config_doc_id_generation: "Configure how document IDs are generated when creating new entities.",
    enable_text_search: "Enable text search for this collection",
    text_search_description: "Allow text search for this collection. If you have not specified a text search delegate, this will use the built-in local text search. This is not recommended for large collections, as it may incur in performance and cost issues.",
    database_id: "Database ID",
    default_text: "(default)",

    custom_actions: "Custom actions",
    more_info: "More info",
    define_custom_actions_cli: "Define your own custom actions by uploading them with the CLI.",
    action_defined_in_code: "This action is defined in code with key",
    add_custom_entity_action: "Add custom entity action",
    remove_this_action: "Remove this action?",
    remove_action_warning: "This will not delete any data, only the action in the CMS",

    subcollections_of: "Subcollections of",
    add_subcollection: "Add subcollection",
    custom_views: "Custom views",
    define_custom_views_cli: "Define your own custom views by uploading them with the CLI.",
    view_defined_in_code: "This view is defined in code with key",
    add_custom_entity_view: "Add custom entity view",
    delete_this_subcollection: "Delete this subcollection?",
    remove_collection_warning: "This will not delete any data, only the collection in the CMS",
    remove_this_view: "Remove this view?",
    remove_view_warning: "This will not delete any data, only the view in the CMS",

    no_collection_selected: "No collection selected",
    code_for_collection: "Code for",
    use_config_define_json: "Use this config to define the collection in JSON format.",
    customise_collection_code: "If you want to customise the collection in code, you can add this collection code to your CMS app configuration.",
    copied: "Copied",

    property_cant_be_edited: "This property can't be edited",
    property_not_editable_description: "You may not have permission to edit it or it is defined in code with the editable flag set to false.",
    delete_this_property: "Delete this property?",
    delete_property_warning: "This will not delete any data, only modify the collection.",
    error_must_specify_id: "You must specify an id for the field",
    error_id_format: "The id can only contain letters, numbers and underscores (_), and not start with a number",
    error_id_already_exists: "There is another field with this ID already",
    error_must_specify_title: "You must specify a title for the field",
    custom_or_other: "Custom/Other",
    select_property_widget: "Select a property widget",
    error_changing_data_type: "This widget uses a different data type than the initially selected widget. This can cause errors with existing data.",
    required: "Required",

    enum_form_dialog: "Enum form dialog",
    imported_data_preview: "Imported data preview",
    entities_with_same_id_overwritten: "Entities with the same id will be overwritten",
    collection_editor: "Collection editor",
    properties_in_this_group: "Properties in this group",
    data_property_mapping: "Data property mapping",
    property_edit_view: "Property edit view",
    all_of_these: "All of these",
    any_of_these: "Any of these",

    only_admins_edit_roles: "Only admins can edit roles",
    error_user_not_found: "User not found",
    role: "Role",
    name_of_this_role: "Name of this role",
    id_of_this_role: "ID of this role",
    create_entities: "Create entities",
    read_entities: "Read entities",
    update_entities: "Update entities",
    delete_entities: "Delete entities",
    all_collections: "All collections",
    create_entities_in_collections: "Create entities in collections",
    access_all_data_in_every_collection: "Access all data in every collection",
    update_data_in_any_collection: "Update data in any collection",
    delete_data_in_any_collection: "Delete data in any collection",
    allow_all_permissions_in_this_collections: "Allow all permissions in this collections",
    all: "All",
    customise_permissions_description: "You can customise the permissions that the users related to this role can perform in the entities of each collection",
    create_collections: "Create collections",
    yes: "Yes",
    no: "No",
    can_user_create_collections: "Can the user create collections",
    edit_collections: "Edit collections",
    only_own_collections: "Only his/her own",
    own: "Own",
    can_user_edit_collections: "Can the user edit collections",
    delete_collections: "Delete collections",
    can_user_delete_collections: "Can the user delete collections",
    error_saving_role: "There was an error saving this role",
    create_role: "Create role",
    update: "Update",

    only_admins_change_roles: "Only admins can change roles",
    must_be_at_least_one_admin: "There must be at least one admin",
    logged_user_not_found: "Logged user not found",
    user: "User",
    user_id: "User ID",
    name_of_this_user: "Name of this user",
    email_of_this_user: "Email of this user",
    roles: "Roles",
    create_user: "Create user",

    users: "Users",
    add_user: "Add user",
    add_role: "Add role",
    is_admin: "Is Admin",
    default_permissions: "Default permissions",
    created_on: "Created on",
    email: "Email",
    id: "ID",
    read: "Read",

    column_in_file: "Column in file",
    map_to_property: "Map to Property",
    default_values: "Default values",
    default_values_description: "You can select a default value for unmapped columns and empty values:",
    property: "Property",
    default_value: "Default value",
    autogenerate_id: "Autogenerate ID",
    id_column_description: "Column that will be used as ID for each document",
    do_not_set_value: "Do not set value",
    set_value_to_true: "Set value to true",
    set_value_to_false: "Set value to false",
    drag_and_drop_file: "Drag and drop a file here or click to upload",
    error_saving_data: "Error saving data",
    retry: "Retry",
    saving_data: "Saving data",
    entities_saved: "entities saved",
    do_not_close_tab: "Do not close this tab or the import will be interrupted",
    import: "Import",
    import_data: "Import data",
    upload_file_description: "Upload a CSV, Excel or JSON file and map it to your existing schema",
    back: "Back",
    next: "Next",
    save_data: "Save data",
    use_column_as_id: "Use this column as ID",
    do_not_import_property: "Do not import this property",
    entities_will_be_overwritten: "Entities with the same id will be overwritten",
    data_imported_successfully: "Data imported successfully",
    export: "Export",
    export_data: "Export data",
    download_table_csv: "Download the the content of this table as a CSV",
    csv: "CSV",
    json: "JSON",
    dates_as_timestamps: "Dates as timestamps",
    dates_as_strings: "Dates as strings",
    flatten_arrays: "Flatten arrays",
    download: "Download",
    large_number_of_documents: "This collections has a large number of documents ({{count}}).",
    include_undefined_values: "Include undefined values",
    submit: "Submit",

    no_filterable_properties: "No filterable properties available",
    apply_filters: "Apply filters",
    list: "List",
    cards: "Cards",
    board: "Board",
    initialize_kanban_order_desc: "This will assign sequential order values to all items that don't have one. Items will maintain their current order within each column.",
    kanban_view_not_available: "Kanban view is not available",
    kanban_view_requires_enum: "Kanban view requires a string property with enum values to group entities into columns. Please add an enum property to your collection schema to use this view.",
    no_enum_values_configured: 'No enum values configured for property "{{property}}"',
    items_need_backfill: "Some items don't have order values. Initialize to enable drag-and-drop reordering.",
    initialize: "Initialize",
    confirm_multiple_delete: "Confirm multiple delete?",
    delete_entity_confirm_title: "Would you like to delete this {{entityName}}?",

    /** AI Collection Generator Popover */
    generate_collection_with_ai: "Generate Collection with AI",
    modify_collection_with_ai: "Modify Collection with AI",
    describe_collection_to_create: "Describe the collection you want to create.",
    describe_changes_to_make: "Describe the changes you want to make to this collection.",
    ai_placeholder_create: "e.g., Create a products collection with name, price, description, and category...",
    ai_placeholder_modify: "e.g., Add a thumbnail image field with storage, make price required...",
    ai_assist: "AI Assist",
    generating: "Generating...",

    /** Recently extracted strings for collection editor */
    this_is_subcollection_of: "This is a subcollection of",
    use_existing_paths_database: "Use one of the existing paths in your database:",
    describe_collection_ai: "Describe your collection to AI:",
    generate_with_ai: "Generate with AI",
    create_from_json_config: "Create from JSON configuration:",
    paste_json_config: "Paste JSON Configuration",
    create_collection_from_file_formats: "Create a collection from a file (csv, json, xls, xslx...)",
    select_template: "Select a template:",
    products: "Products",
    collection_products_subtitle: "A collection of products with images, prices and stock",
    collection_users_subtitle: "A collection of users with emails, names and roles",
    blog_posts: "Blog posts",
    collection_blog_posts_subtitle: "A collection of blog posts with images, authors and complex content",
    pages: "Pages",
    collection_pages_subtitle: "A collection of pages with images, authors and complex content",
    continue_from_scratch: "Continue from scratch",

    /** Admin views config */
    cms_users: "CMS Users",
    roles_menu: "Roles",
    project_settings: "Project settings",
    firestore_manager: "Firestore Manager",
    manage_your_firestore_data: "Manage your Firestore data",

    // ─── FireCMS Cloud Login ──────────────────────────────────────
    build_admin_panel_in_minutes: "Build Your Firebase Admin Panel in Minutes",
    go_live_instantly: "Go live instantly:",
    create_production_ready_back_offices: "Create production-ready back-offices",
    without_the_frontend_hassle: "without the frontend hassle.",
    automatic_setup: "Automatic setup",
    from_your_existing_firestore_data: "from your existing Firestore data.",
    seamless_real_time_firebase_integration: "Seamless real-time Firebase integration.",
    intuitive_spreadsheet_like_ui: "Intuitive spreadsheet-like UI",
    your_whole_team_can_use: "your whole team can use.",
    focus_on_your_app: "Focus on your app,",
    not_the_admin_panel: "not the admin panel.",
    join_our_newsletter: "Join our newsletter. No spam, only important updates!",
    by_signing_in_you_agree_to_our: "By signing in you agree to our",
    terms_and_conditions: "Terms and Conditions",
    and_our: "and our",
    privacy_policy: "Privacy policy",
    firecms_cloud_google_disclosure: "FireCMS Cloud use and transfer to any other app of information received from Google APIs will adhere to",
    google_api_services_user_data_policy: "Google API Services User Data Policy",
    including_the_limited_use_requirements: "including the Limited Use requirements.",
    email_password: "Email/password",
    sign_in_with_google: "Sign in with Google",

    // --- Auth error messages ---
    auth_user_not_found: "User not found",
    auth_wrong_password: "Wrong password. Please try again.",
    auth_user_disabled: "User disabled. Please contact support.",
    auth_account_exists_with_different_credential: "Account exists with different sign in method",
    auth_email_already_in_use: "The email is already in use",
    auth_google_permissions_required: "You need to grant additional permissions in order to manage your Google Cloud projects",
    auth_invalid_email_password: "Please enter both email and password",
    auth_enter_email_first: "Please enter your email first",
    auth_password_reset_sent: "Password reset email sent",
    auth_sign_in_account: "Sign in to your account",
    auth_create_new_account: "Create a new account",
    auth_password: "Password",
    auth_reset_password: "Reset password",
    auth_new_user: "New user?",
    auth_have_account: "Have an account?",
    auth_sign_in: "Sign In",
    auth_sign_up: "Sign Up",

    // --- SaaS Subscriptions ---
    subscriptions: "Subscriptions",
    manage_your_subscriptions_in_stripe: "Manage your subscriptions in Stripe",
    go_to_your_stripe_portal: "Go you your user Stripe portal to view billing history. Note that you can also manage subscriptions associated with the logged in user.",
    your_pro_licenses: "Your PRO licenses",
    create_new_license: "Create new license",
    create_subscriptions_in_this_section: "Create subscriptions in this section only for self hosted FireCMS PRO. If you are using FireCMS Cloud, you can upgrade your project from within the project settings.",
    if_you_are_an_agency: "If you are an agency, you are free to resell your license to your customers.",
    if_you_have_any_questions: "If you have any questions or need help, please contact us at",
    you_have_not_created_any_pro_licenses: "You have not created any FireCMS PRO licenses yet",
    archive: "Archive",
    licensed_projects_lowercase: "licensed projects",
    manage: "Manage",
    update_payment_method: "Update payment method",
    your_firecms_cloud_projects: "Your FireCMS Cloud projects",
    status_active: "Active",
    status_trialing: "Trialing",
    status_past_due: "Past due",
    status_canceled: "Canceled",
    status_unpaid: "Unpaid",
    status_incomplete: "Incomplete",
    status_incomplete_expired: "Incomplete expired",
    status_unknown: "Unknown",

    plan_free: "Not subscribed",
    plan_cloud_plus: "Subscribed",
    plan_pro: "Pro",
    plan_unknown: "Unknown Plan",

    auto_setup_collections_button: "Automatically set up collections",
    auto_setup_collections_title: "Automatically set up collections?",
    auto_setup_collections_desc: "This will automatically create collection configs for collections that are <b>NOT</b> already mapped",
    setting_up_collections: "Setting up collections",
    setting_up_collection: "Setting up {{name}}",
    no_collections_found_to_setup: "No collections found to setup.",
    collections_have_been_setup: "Collections have been automatically setup.",
    error_setting_up_collections: "Error automatically setting up collections",
    setup_collections_title: "Set up collections",
    setup_collections_select_desc: "Select which collections to automatically set up:",
    select_all: "Select all",
    deselect_all: "Deselect all",
    setup_collections_confirm: "Set up ({{count}})",
    collection_setup_success: "{{name}} has been set up",
    go_to_collection: "Go to collection",

    // --- Home Suggestions ---
    add_your: "Add your",
    database_collections: "database collections",
    to_firecms: "to FireCMS",
    no_unmapped_collections: "No unmapped collections in the database",
    query_and_update_with_datatalk: "Query and update your data in natural language with",

    // --- SaaS Welcome ---
    welcome_to_firecms: "Welcome to FireCMS Cloud",
    admin_panel_ready_bring_data: "Your admin panel is ready. Let's bring in your data.",
    admin_panel_ready_get_started: "Your admin panel is ready. Here's how to get started.",
    auto_detect_collections: "Auto-detect collections",
    auto_detect_collections_desc: "Let AI scan your database and automatically generate collection schemas.",
    create_a_collection: "Create a collection",
    create_collection_desc: "Manually define your first collection from scratch using the visual editor.",
    read_the_docs: "Read the docs",
    read_the_docs_desc: "Learn how to customize fields, views, actions and more.",
    explore_docs: "Explore docs",
    want_to_customize_with_code: "Want to customize with code? Run",
    to_scaffold_a_local_project: "to scaffold a local project.",

    // ─── Collection Editor — Validation ──────────────────────────
    validation: "Validation",
    unique: "Unique",
    required_message: "Required message",
    required_tooltip: "You won't be able to save this entity if this value is not set",
    unique_tooltip: "There cannot be multiple entities with the same value",
    lowercase: "Lowercase",
    uppercase: "Uppercase",
    trim: "Trim",
    exact_length: "Exact length",
    min_length: "Min length",
    max_length: "Max length",
    matches_regex: "Matches regex",
    not_valid_regexp: "Not a valid regexp",
    regex_helper: "e.g. /^\\d+$/ for digits only",
    min_value: "Min value",
    max_value: "Max value",
    less_than: "Less than",
    more_than: "More than",
    positive_value: "Positive value",
    negative_value: "Negative value",
    integer_value: "Integer value",

    // ─── Collection Editor — Property Edit ───────────────────────
    invalid_regular_expression: "Invalid regular expression",
    must_specify_target_collection: "You must specify a target collection for the field",
    need_specify_repeat_field: "You need to specify a repeat field",
    need_specify_block_properties: "You need to specify the properties of this block",
    incomplete_condition: "Incomplete condition - please select a field",
    field_name: "Field name",

    // ─── Collection Editor — Display & Config ────────────────────
    kanban_column_property: "Kanban Column Property",
    select_a_property: "Select a property",
    kanban_property_not_found: "Property \"{{property}}\" does not exist or is not an enum string property. Please select a valid property or clear the selection.",
    no_enum_string_properties: "No enum string properties found. Add a string property with enumValues to use Kanban view.",
    kanban_column_description: "Select a string property with enum values to group entities into columns",
    create_property: "+ Create \"{{property}}\" property",
    order_property: "Order Property",
    order_property_not_found: "Property \"{{property}}\" does not exist or is not a number property. Please select a valid property or clear the selection.",
    no_number_properties: "No number properties found. Add a number property to enable ordering.",
    order_property_description: "Select a number property to persist the order of items",
    display_settings: "Display settings",
    default_row_size: "Default row size",
    side_dialog_width: "Side dialog width",
    side_dialog_width_description: "Optionally define the width (in pixels) of entities side dialog. Default is 768px",
    inline_editing_enabled: "Data can be edited directly in the table view",
    inline_editing_disabled: "Data can be edited only in the form view",
    inline_editing_description: "Allow editing data directly in the table view, without opening the form view.",
    include_json_view: "Include JSON view",
    no_json_view: "Do not include JSON view",
    json_view_description: "Include the JSON representation of the document.",
    not_found_suffix: "not found",

    // ─── Editor ─────────────────────────────────────────────────
    editor_text: "Text",
    editor_text_description: "Just start typing with plain text.",
    editor_heading_1: "Heading 1",
    editor_heading_1_description: "Big section heading.",
    editor_heading_2: "Heading 2",
    editor_heading_2_description: "Medium section heading.",
    editor_heading_3: "Heading 3",
    editor_heading_3_description: "Small section heading.",
    editor_todo_list: "To-do List",
    editor_todo_list_description: "Track tasks with a to-do list.",
    editor_bullet_list: "Bullet List",
    editor_bullet_list_description: "Create a simple bulleted list.",
    editor_numbered_list: "Numbered List",
    editor_numbered_list_description: "Create a list with numbering.",
    editor_quote: "Quote",
    editor_quote_description: "Capture a quote.",
    editor_code: "Code",
    editor_code_description: "Capture a code snippet.",
    editor_image: "Image",
    editor_image_description: "Upload an image from your computer.",
    editor_multiple: "Multiple",
    editor_link: "Link",
    editor_save: "Save",
    editor_cancel: "Cancel",
    editor_remove_link: "Remove link",
    editor_paste_or_type_link: "Paste or type link",
    editor_open_in_new_window: "Open in new window",
    editor_bold: "Bold",
    editor_italic: "Italic",
    editor_underline: "Underline",
    editor_strikethrough: "Strikethrough",
    editor_autocomplete: "Autocomplete",
    editor_autocomplete_description: "Add text based on the context.",

    // ─── Text Search Dialog ─────────────────────────────────────
    text_search_dialog_title: "Enable text search",
    text_search_local_not_recommended: "Local text search is not recommended for large collections.",
    text_search_local_fetch_warning: "Note that enabling local text search will need to fetch all documents from your collection and store them in the browser. This can be inefficient for large collections. It can also incur in additional costs.",
    text_search_external_suggestion: "For larger collections, you are encouraged to use an external search engine such as Algolia or Elastic Search, and assign a search delegate to your config.",
    text_search_local_description: "Local text search is the simplest way to enable text search in your collection. It loads all documents in the collection in the browser and performs the search locally. This is the recommended option for small collections.",
    text_search_own_implementation: "You have implemented your own text search controller. You enable text search for your collection.",
    text_search_enable_for_collection: "Enable for this collection",
    text_search_enable_for_project: "Enable for project",
    text_search_enabled_snackbar: "Local text search enabled",

    // ─── Cloud Project Settings ──────────────────────────────────
    settings_subscription_plan: "Subscription Plan",
    settings_subscribed_to: "You are currently subscribed to",
    settings_no_active_subscription: "Currently there is no active subscription for this project.",
    settings_trial_valid_until: "Your trial is valid until {{date}}.",
    settings_features_intro: "These are some of the features you are already enjoying by using FireCMS Cloud",
    settings_feature_managed_service: "Managed always up-to-date service",
    settings_feature_local_text_search: "Local text search",
    settings_feature_unlimited_users_roles: "Unlimited users and roles",
    settings_feature_theme_logo: "Theme and logo customization",
    settings_feature_custom_fields_views: "Custom form fields and custom views",
    settings_feature_secondary_databases: "Secondary databases",
    settings_feature_ai_content: "AI content generation with OpenAI and Google",
    settings_feature_unlimited_export: "Unlimited data export",
    settings_feature_appcheck: "AppCheck",
    settings_heading: "Settings",
    settings_project_name: "Project name",
    settings_default_language: "Default language",
    settings_default_language_caption: "Select the base language for this project. Users can override this preference in their personal settings.",
    settings_enable_local_text_search: "Enable local text search",
    settings_local_text_search_caption: "Enable local text search for all collections. This will allow you to search text fields in your collections using the FireCMS search bar. Note that this feature can incur in higher read counts, as it will index all text fields in your collections.",
    settings_doc_history_all_collections: "Document history enabled for all collections",
    settings_doc_history_caption: "When true, all collections will have the history enabled by default. You can override this setting in each collection. History will be saved in the __history subcollection of each document.",
    settings_theme: "Theme",
    settings_primary_color: "Primary color",
    settings_secondary_color: "Secondary color",
    settings_sample_theme_components: "Sample theme components",
    settings_drag_drop_logo: "Drag and drop your logo here",
    settings_create_subscription: "Create a subscription",
    settings_stripe_disclaimer: "You will be redirected to Stripe to complete your subscription. Billed monthly based on your maximum users this month. Cancel anytime - you've already paid for the current billing period, so no additional charges after cancellation.",
    settings_subscription_is: "The subscription is",
    settings_next_payment_on: "The next payment is on {{date}}.",
    settings_seats_count: "You have",
    settings_seat: "seat",
    settings_seats: "seats",
    settings_per_seat: "at {{price}}/seat/{{interval}}",
    settings_current_price: "The current price is",
    settings_per_user_usage: "per user (usage-based).",
    settings_cancelled_active_until: "This subscription was cancelled and will be active until {{date}}.",
    settings_no_additional_charges: "No additional charges will apply after cancellation.",
    settings_manage_subscription: "Manage subscription",
    settings_security_rules: "Security rules",
    settings_security_rules_description: "FireCMS uses Firebase security rules to restrict access to data. When creating a new user through FireCMS, the custom claim fireCMSUser is added to the user in the client project. By adding the following security rules to your project, you ensure FireCMS users can access data through FireCMS.",
    settings_security_rules_add_domain: "Remember to add the domain {{domain}} to your provider allowed domains",
    settings_security_rules_caption: "This rules restrict access to data to FireCMS users only, but does not enforce the permissions at the database level. The permissions are enforced in the frontend though, which will work fine for most projects. If you need to enforce the permissions at the database level, you can modify these security rules yourself to suit your needs. The roles assigned to a user are set as a custom claim in the Firebase auth token, so you can use them in your security rules.",
    settings_appcheck: "AppCheck",
    settings_appcheck_description: "You can enable AppCheck to protect your Firebase services from abuse. Check how to configure it in the Firebase documentation. When you have a provider set, you can enable it here. You will need to provide a secret in your Firebase project settings, and a site key in the FireCMS config.",
    settings_appcheck_add_domain: "Remember to add the domain {{domain}} to your provider allowed domains",
    settings_appcheck_enable: "Enable AppCheck",
    settings_appcheck_site_key: "Site key",
    settings_appcheck_update: "Update AppCheck",
    settings_appcheck_refresh_note: "You might need to refresh the page to see the changes, after saving.",
    settings_appcheck_updated: "AppCheck updated",
    settings_appcheck_error: "Error updating AppCheck",

    // --- Permission Error View ---
    missing_firestore_security_rules: "Missing Firestore Security Rules",
    firecms_cloud_requires_security_rule: "FireCMS Cloud requires a specific security rule in your Firestore to grant access to authenticated users. The collection",
    cannot_be_accessed_without_it: "cannot be accessed without it.",
    required_security_rule: "Required security rule",
    fix_automatically: "Fix automatically",
    open_firebase_rules: "Open Firebase Rules",
    security_rules_updated_successfully: "Security rules updated successfully! Please refresh the page to load your data.",
    sec_rules_fixing: "Fixing...",
    sec_rules_fixed: "Fixed!",

    // ─── GCP Marketplace ─────────────────────────────────────────
    marketplace_managed_by_gcp: "Managed via GCP Marketplace",
    marketplace_billing_note: "Your subscription is managed through Google Cloud Marketplace. Plan changes, billing, and cancellation are handled in the GCP Console.",
    marketplace_manage_in_gcp_console: "Manage in GCP Console",
    marketplace_plan_changes_note: "To change your plan or cancel, visit your GCP Marketplace orders.",
    marketplace_welcome_title: "Welcome from GCP Marketplace!",
    marketplace_welcome_subtitle: "Your Google Cloud Marketplace subscription is active. Select an existing project or create a new one to link it.",
    marketplace_select_or_create_project: "Select or create a project",
    marketplace_link_project: "Link project",
    marketplace_linking: "Linking project…",
    marketplace_link_success: "Project linked successfully! Redirecting…",
    marketplace_link_error: "Error linking project. Please try again.",
    marketplace_no_account_id: "No GCP Marketplace account ID found. Please start from the GCP Marketplace.",
};
